/**
 * URS-F352 — Tabla personalizada NxM con cabeceras en fila 1 y columna 1.
 * Cada celda interior puede ser texto, número o Sí/No.
 *
 * Exporta:
 *   - CustomTableDesigner: editor de la estructura (usado en QuestionEditor
 *     del Banco de Preguntas y del editor de preguntas dentro del estudio).
 *   - CustomTableRenderer: renderiza la tabla rellena al voluntario en el
 *     panel de cuestionarios y en las encuestas públicas.
 *   - customTableMissing(q, ans): helper de validación para missingAnswers().
 *   - cellKey(rowHeader, colHeader): clave canónica de cada celda.
 */
import React from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Plus, Minus } from '@phosphor-icons/react';

export const cellKey = (rowHeader, colHeader) =>
  `${(rowHeader || '').trim()}|${(colHeader || '').trim()}`;

const CELL_TYPE_OPTIONS = [
  { v: 'text',   labelEs: '📝 Texto',  labelEn: '📝 Text' },
  { v: 'yes_no', labelEs: '✅ Sí/No',  labelEn: '✅ Yes/No' },
  { v: 'number', labelEs: '🔢 Número', labelEn: '🔢 Number' },
];

const ensureMatrix = (q) => {
  const rows = Math.max(2, Math.min(10, q.table_rows || 2));
  const cols = Math.max(2, Math.min(10, q.table_cols || 2));
  const headerRow = [...(q.table_headers_row || [])];
  while (headerRow.length < cols) headerRow.push('');
  headerRow.length = cols;
  const headerCol = [...(q.table_headers_col || [])];
  while (headerCol.length < rows - 1) headerCol.push('');
  headerCol.length = rows - 1;
  const types = [...(q.table_cell_types || [])];
  while (types.length < rows - 1) types.push([]);
  for (let i = 0; i < rows - 1; i++) {
    types[i] = [...(types[i] || [])];
    while (types[i].length < cols - 1) types[i].push('text');
    types[i].length = cols - 1;
  }
  types.length = rows - 1;
  return { rows, cols, headerRow, headerCol, types };
};

export function CustomTableDesigner({ value, onChange, lang = 'es' }) {
  const { rows, cols, headerRow, headerCol, types } = ensureMatrix(value);

  const apply = (patch) => onChange({
    table_rows: rows,
    table_cols: cols,
    table_corner: value.table_corner || '',
    table_headers_row: headerRow,
    table_headers_col: headerCol,
    table_cell_types: types,
    ...patch,
  });

  const setSize = (newR, newC) => {
    const r = Math.max(2, Math.min(10, newR));
    const c = Math.max(2, Math.min(10, newC));
    apply({ table_rows: r, table_cols: c });
  };

  const setHeaderRow = (i, v) => {
    const arr = [...headerRow]; arr[i] = v; apply({ table_headers_row: arr });
  };
  const setHeaderCol = (i, v) => {
    const arr = [...headerCol]; arr[i] = v; apply({ table_headers_col: arr });
  };
  const setCellType = (r, c, v) => {
    const m = types.map(row => [...row]); m[r][c] = v; apply({ table_cell_types: m });
  };

  return (
    <div className="flex-1 border border-violet-200 bg-violet-50/40 rounded-sm p-2 space-y-2" data-testid="custom-table-designer">
      <div className="flex items-center gap-2 text-[11px]">
        <span className="font-semibold text-violet-700">{lang === 'es' ? 'Tabla' : 'Table'}</span>
        <span className="flex items-center gap-1">
          <span>{lang === 'es' ? 'Filas' : 'Rows'}:</span>
          <Button size="sm" variant="outline" className="h-5 w-5 p-0" onClick={() => setSize(rows - 1, cols)} disabled={rows <= 2}><Minus size={10} /></Button>
          <span className="w-5 text-center font-mono">{rows}</span>
          <Button size="sm" variant="outline" className="h-5 w-5 p-0" onClick={() => setSize(rows + 1, cols)} disabled={rows >= 10}><Plus size={10} /></Button>
        </span>
        <span className="flex items-center gap-1">
          <span>{lang === 'es' ? 'Cols' : 'Cols'}:</span>
          <Button size="sm" variant="outline" className="h-5 w-5 p-0" onClick={() => setSize(rows, cols - 1)} disabled={cols <= 2}><Minus size={10} /></Button>
          <span className="w-5 text-center font-mono">{cols}</span>
          <Button size="sm" variant="outline" className="h-5 w-5 p-0" onClick={() => setSize(rows, cols + 1)} disabled={cols >= 10}><Plus size={10} /></Button>
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="text-[10px] w-full" data-testid="custom-table-designer-grid">
          <tbody>
            {Array.from({ length: rows }).map((_, r) => (
              <tr key={r}>
                {Array.from({ length: cols }).map((__, c) => {
                  if (r === 0 && c === 0) {
                    return <td key="corner" className="p-0.5 border border-violet-300 bg-violet-100">
                      <Input
                        value={value.table_corner || ''}
                        onChange={e => apply({ table_corner: e.target.value })}
                        placeholder={lang === 'es' ? 'Esquina' : 'Corner'}
                        className="h-6 text-[10px] border-0 bg-transparent"
                        data-testid="ct-corner"
                      />
                    </td>;
                  }
                  if (r === 0) {
                    return <td key={c} className="p-0.5 border border-violet-300 bg-violet-100">
                      <Input
                        value={headerRow[c] || ''}
                        onChange={e => setHeaderRow(c, e.target.value)}
                        placeholder={`Col ${c}`}
                        className="h-6 text-[10px] border-0 bg-transparent font-semibold"
                        data-testid={`ct-hrow-${c}`}
                      />
                    </td>;
                  }
                  if (c === 0) {
                    return <td key="hc" className="p-0.5 border border-violet-300 bg-violet-100">
                      <Input
                        value={headerCol[r - 1] || ''}
                        onChange={e => setHeaderCol(r - 1, e.target.value)}
                        placeholder={`Fila ${r}`}
                        className="h-6 text-[10px] border-0 bg-transparent font-semibold"
                        data-testid={`ct-hcol-${r - 1}`}
                      />
                    </td>;
                  }
                  const t = (types[r - 1] && types[r - 1][c - 1]) || 'text';
                  return <td key={c} className="p-0.5 border border-violet-300 bg-white">
                    <select
                      value={t}
                      onChange={e => setCellType(r - 1, c - 1, e.target.value)}
                      className="w-full h-6 text-[10px] bg-transparent"
                      data-testid={`ct-celltype-${r - 1}-${c - 1}`}
                    >
                      {CELL_TYPE_OPTIONS.map(opt =>
                        <option key={opt.v} value={opt.v}>{lang === 'es' ? opt.labelEs : opt.labelEn}</option>
                      )}
                    </select>
                  </td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[9px] text-stone-500 italic">
        {lang === 'es'
          ? 'Cabeceras en violeta. Cada celda interior tendrá el tipo elegido al rellenar el cuestionario.'
          : 'Headers in violet. Each inner cell uses its chosen type when filling.'}
      </p>
    </div>
  );
}

export function CustomTableRenderer({ question, answers, onChange, readOnly = false, lang = 'es' }) {
  const { rows, cols, headerRow, headerCol, types } = ensureMatrix(question);
  const a = answers || {};
  const setVal = (k, v) => onChange && onChange({ ...a, [k]: v });

  return (
    <div className="overflow-x-auto" data-testid={`custom-table-render-${question.question_id || ''}`}>
      <table className="text-xs w-full border-collapse">
        <thead>
          <tr>
            <th className="border border-stone-300 bg-stone-100 p-1 text-left">{question.table_corner || ''}</th>
            {headerRow.slice(1).map((h, ci) => (
              <th key={ci} className="border border-stone-300 bg-stone-100 p-1 text-center font-semibold">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows - 1 }).map((_, ri) => (
            <tr key={ri}>
              <td className="border border-stone-300 bg-stone-50 p-1 font-semibold">{headerCol[ri] || ''}</td>
              {Array.from({ length: cols - 1 }).map((__, ci) => {
                const t = (types[ri] && types[ri][ci]) || 'text';
                const k = cellKey(headerCol[ri], headerRow[ci + 1]);
                const v = a[k];
                if (readOnly) {
                  return <td key={ci} className="border border-stone-300 p-1 text-center">{v ?? '—'}</td>;
                }
                if (t === 'yes_no') {
                  return <td key={ci} className="border border-stone-300 p-1 text-center">
                    <div className="flex gap-1 justify-center">
                      {['si', 'no'].map(opt => (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setVal(k, opt)}
                          data-testid={`ct-cell-${ri}-${ci}-${opt}`}
                          className={`px-2 py-0.5 text-[10px] rounded-sm border ${v === opt ? (opt === 'si' ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-rose-600 text-white border-rose-700') : 'bg-white border-stone-300'}`}
                        >{opt === 'si' ? (lang === 'es' ? 'Sí' : 'Yes') : 'No'}</button>
                      ))}
                    </div>
                  </td>;
                }
                return <td key={ci} className="border border-stone-300 p-0.5">
                  <Input
                    type={t === 'number' ? 'number' : 'text'}
                    value={v ?? ''}
                    onChange={e => setVal(k, e.target.value)}
                    data-testid={`ct-cell-${ri}-${ci}-input`}
                    className="h-6 text-[11px] border-0"
                  />
                </td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Helper para missingAnswers(): devuelve labels de celdas vacías de una
// pregunta custom_table dada su respuesta actual.
export function customTableMissing(q, ans) {
  const { rows, cols, headerRow, headerCol, types } = ensureMatrix(q);
  const obj = (typeof ans === 'object' && ans) || {};
  const labels = [];
  for (let ri = 0; ri < rows - 1; ri++) {
    for (let ci = 0; ci < cols - 1; ci++) {
      const t = (types[ri] && types[ri][ci]) || 'text';
      const k = cellKey(headerCol[ri], headerRow[ci + 1]);
      const v = obj[k];
      const empty = v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
      if (empty) {
        // Excepción: si la celda es yes_no, "no" también es válido — solo
        // se considera vacía si no hay valor.
        labels.push(`${headerCol[ri] || '?'} / ${headerRow[ci + 1] || '?'} (${t})`);
      }
    }
  }
  return labels;
}
